import React, { useEffect, useState } from "react";
import WalletBalanceCard from "../../component/dashboard/WalletBalanceCard";
import CurrencyToggle from "../../component/currencyToggle";
import useGetRequest from "../../hooks/useGetRequest";
import currencyPairCheck from "../../utilities/currencyPairCheck";
import { useDispatch, useSelector } from "react-redux";
import AdsCarousel from "../../component/dashboard/AdsCarousel";
import UsdcRightMock from "../../ads/UsdcRightMock";
import USDCAd from "../../ads/USDCAd";
import { handleFetchDashboardStats, fetchUserProfile } from "../../api/fetchers";
import { Box, Moneys, Receipt1 } from "iconsax-reactjs";
import { useNavigate } from "react-router-dom";

import TopBar from "../../component/dashboard/TopBar";
import Kpis from "../../component/dashboard/Kpis";
import DashboardModals from "../../component/dashboard/DashboardModals";
import { normalizeAccount } from "../../utilities/normalizeAccount";
import KESUSDAd from "../../ads/KESUSDAd";
import KESUSDRightMock from "../../ads/KESUSDRightMock";
import OutstandingMaintenanceFeeModal from "../../component/dashboard/OutstandingMaintenanceFeeModal";
import { NGIcon, GhIcon, KESIcon, USIcon, USDCIcon, USDTIcon } from "asset/icons";
import { currencyToDisplay } from "../../component/flex/utils";

const iconMap = {
  NGN: NGIcon,
  GHC: GhIcon,
  GHS: GhIcon,
  KES: KESIcon,
  USD: USIcon,
  USDC: USDCIcon,
  USDT: USDTIcon,
};

export default function DashboardTop() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // data state
    const [userProfile, setUserProfile] = useState(null);
    const [stats, setStats] = useState({ activeOrders: 0, activeLoans: 0, unpaidInvoices: 0 });
    const [statsLoading, setStatsLoading] = useState(false);

    // UI state (modals)
    const [showFundModal, setShowFundModal] = useState(false);
    const [showFundingMethodModal, setShowFundingMethodModal] = useState(false);
    const [showFundViaConversionModal, setShowFundViaConversionModal] = useState(false);
    const [showReceiveWalletModal, setShowReceiveWalletModal] = useState(false);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showPinModal, setShowPinModal] = useState(false);
    const [onPinSuccess, setOnPinSuccess] = useState(null);
  const [pinError, setPinError] = useState("");
  const [showDrawer, setShowDrawer] = useState(false);
  const [showOutstandingFeeModal, setShowOutstandingFeeModal] = useState(false);

  const {
    users: user,
    business,
    currency: defaults,
    businessSwitching,
    wallets,
    isLoading
  } = useSelector((s) => s);
  const hasSelectedWallet = (wallets || []).some(
    (wallet) =>
      String(wallet?.currency || "").toUpperCase() ===
      String(defaults || "").toUpperCase()
  );

  const isBusinessApproved = business?.status === "APPROVED" && business?.hasPaidOpeningFee;
    const isCryptoWallet = defaults === "USDT" || defaults === "USDC";
    const isUsd = defaults === "USD" || defaults === "KES";
    const country = String(
      business?.businessCountry?.name || business?.country || ""
    ).toLowerCase();
    const countryCode = String(
      business?.businessCountry?.code || business?.countryCode || ""
    ).toUpperCase();
    const isUsBusiness =
      [
        "united states",
        "united state",
        "united states of america",
        "us",
        "usa",
      ].includes(country) ||
      countryCode === "US" ||
      countryCode === "USA";

    const hasUsdWallet = (wallets || []).some(
      (wallet) => String(wallet?.currency || "").toUpperCase() === "USD"
    );
    const hasStaleNgnDefault = isUsBusiness && defaults === "NGN" && hasUsdWallet;

    useEffect(() => {
        if (!business?._id || !hasStaleNgnDefault) return;

        dispatch({ type: "CURRENCY", payload: "USD" });
        localStorage.setItem("default", "USD");
    }, [business?._id, dispatch, hasStaleNgnDefault]);

    // wallet fetch
    const [fetchAccount, accountResponse, accountLoading] = useGetRequest();
    useEffect(() => {
        if (businessSwitching || !business?._id || !defaults || !hasSelectedWallet) return;
        if (hasStaleNgnDefault) return;

        const controller = new AbortController();
        fetchAccount(`/v1/wallets/account?currency=${encodeURIComponent(defaults)}`, {
            headers: { "x-business-id": business._id },
            signal: controller.signal,
        });
        return () => controller.abort();
    }, [business?._id, businessSwitching, defaults, hasSelectedWallet, hasStaleNgnDefault]);

    // stats fetch
    useEffect(() => {
        if (businessSwitching || !business?._id || !defaults) return;
        if (hasStaleNgnDefault) return;

        const controller = new AbortController();
        (async () => {
            try {
                setStatsLoading(true);
                const resp = await handleFetchDashboardStats({
                    businessId: business._id,
                    currency: defaults,
                    signal: controller.signal,
                });
                const p = resp?.data ?? resp ?? {};
                setStats({
                    activeOrders:
                        p.activeOrder ?? p.active_orders ?? p.orders?.active ?? p.ordersActive ?? 0,
                    activeLoans:
                        p.activeLoan ?? p.active_loans ?? p.loans?.active ?? p.loansActive ?? 0,
                    unpaidInvoices:
                        p.unpaidInvoice ?? p.unpaid_invoices ?? p.invoices?.unpaid ?? p.unpaid ?? 0,
                    totalBalance: p.totalBalance,
                    walletBalance: p.totalWalletBalance,
                    creditBalance: p.totalCreditBalance,
                    overduePayments: p.overduePayment,
                });
            } finally {
                setStatsLoading(false);
            }
        })();
        return () => controller.abort();
    }, [business?._id, businessSwitching, defaults, hasStaleNgnDefault]);

    // user profile
    useEffect(() => {
        (async () => {
            try {
                setUserProfile(await fetchUserProfile());
            } catch {}
        })();
    }, []);

    // derived wallet/account
    const fallbackWallet = (wallets || []).find(
      (w) => String(w?.currency || "").toUpperCase() === String(defaults || "").toUpperCase()
    );
    const account =
      normalizeAccount(accountResponse, defaults, business?.name) ||
      normalizeAccount(fallbackWallet, defaults, business?.name);
    const bankName = account?.bankName || "";
    const accountNumber = account?.accountNumber || "";
    const branchName = account?.branchName;
    const accountName = account?.accountName || business?.name || "";
    const balanceAmount = account?.balance ?? 0;
    const bankAddress = account?.bankAddress || "";
    const bankCode = account?.bankCode;
    const routingNumber = account?.routingNumber;
    const paymentRails = account?.paymentRails;

    const { symbol, isCrypto } = currencyPairCheck(defaults, { returnType: "full" });
    const rateInDollars = isCrypto ? balanceAmount * 1.0067 : undefined;

    const handleSelectTransfer = (type) => {
        if (!isBusinessApproved) return;

        if (isCryptoWallet) {
            const map = {
                wallet: "/payment/send-crypto/crypto-wallet/amount",
                fiat: "/payment/send-crypto/fiat-wallet/amount",
            };
            navigate(map[type] || "/payment/send-crypto");
            return;
        }
        if (type === "cross") {
            navigate("/payment/send-money/cross-border/bank-recipient/amount", {
                state: { default: defaults, balance: balanceAmount, type },
            });
            return;
        }
        navigate("/payment/send-money", {
            state: { type, default: defaults, balance: balanceAmount },
        });
    };


  const feesToDisplay = React.useMemo(() => {
    const walletsList = wallets ?? [];

    const filtered = walletsList.filter(
      (item) => Number(item?.outstandingPayment || 0) > 0
    );

    if (filtered.length > 0) {
      return filtered.map((item) => {
        const curr = String(item.currency || "").toUpperCase();
        const amt = Number(item.outstandingPayment || 0);
        return {
          currency: item.currency || curr,
          amount: currencyToDisplay(item.currency, amt),
          Icon: iconMap[curr] || NGIcon,
        };
      });
    }

    return [];
  }, [wallets]);

  const hasViewedMaintenanceFeeInformation = business?.hasViewedMaintenanceFeeInformation;

  useEffect(() => {
    if (isLoading || !isBusinessApproved) return;
    const isDismissed = sessionStorage.getItem("hasDismissedOutstandingFeeModal");
    if (isDismissed === "true") {
      setShowOutstandingFeeModal(false);
      return;
    }

    const walletsList = wallets ?? [];
    const hasOutstanding = walletsList.some(
      (item) => Number(item?.outstandingPayment || 0) > 0
    );

    setShowOutstandingFeeModal(hasOutstanding && hasViewedMaintenanceFeeInformation);
  }, [wallets, isLoading, isBusinessApproved, hasViewedMaintenanceFeeInformation]);


    const openFund = () => {
        if (!isBusinessApproved) return;
        isCryptoWallet ? setShowFundingMethodModal(true) : setShowFundModal(true);
    };
    const openGenerateStatement = () => setShowGenerateModal(true);
  const openAccountDetails = () => setShowDrawer(true);

  const handleCloseMaintenaceFeeModal = () => {
    sessionStorage.setItem("hasDismissedOutstandingFeeModal", "true");
    setShowOutstandingFeeModal(false);
  }

    return (
        <>
            <section className="w-full sm:px-0">
                <TopBar
                    isBusinessApproved={isBusinessApproved}
                    isCryptoWallet={isCryptoWallet}
                    symbol={symbol}
                    onSelectTransfer={handleSelectTransfer}
                    onNavigate={navigate}
                    userName={user.firstName}
                    businessName={business.name}
                />

                <div className="mt-6">
                    <CurrencyToggle
                        dropDownClassName="shadow-dropdown2 !left-0 !top-[109%]"
                        isDisabled={!isBusinessApproved}
                        fetchWallet
                        businessId={business?._id}
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 mt-2 md:grid-cols-12 lg:gap-4">
                    {/* Wallet */}
                    <div className="col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-4">
                        <div className="h-full [&>*]:h-full [&>*]:flex [&>*]:flex-col">
                            <WalletBalanceCard
                                accountLoading={accountLoading}
                                isBusinessApproved={isBusinessApproved}
                                isCryptoWallet={isCryptoWallet}
                                isUsd={isUsd}
                                bankName={bankName}
                                accountNumber={accountNumber}
                                balanceAmount={balanceAmount}
                                overduePayments={stats.overduePayments}
                                totalBalance={stats.totalBalance}
                                walletBalance={stats.walletBalance}
                                creditBalance={stats.creditBalance}
                                symbol={symbol}
                                isCrypto={isCrypto}
                                rateInDollars={rateInDollars}
                                onLoan={() => navigate("/finance/credit/request")}
                                onFund={openFund}
                                onConvert={() => navigate("/convert-currency")}
                                onGenerateStatement={openGenerateStatement}
                                onAccountDetails={openAccountDetails}
                                onSelectTransfer={handleSelectTransfer}
                                onPay={() => navigate("/finance/credit/")}
                            />
                        </div>
                    </div>

                    {/* KPIs + Ad */}
                    <div className="col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-8">
                        <div className="flex flex-col h-full">
                            <Kpis
                                stats={stats}
                                loading={statsLoading}
                                tiles={[
                                    {
                                        key: "activeOrders",
                                        label: "Active orders",
                                        icon: <Box size="28" color="#6F739B" variant="TwoTone" />,
                                    },
                                    {
                                        key: "activeLoans",
                                        label: "Active loans",
                                        icon: (
                                            <Moneys size="28" color="#6F739B" variant="TwoTone" />
                                        ),
                                    },
                                    {
                                        key: "unpaidInvoices",
                                        label: "Unpaid invoices",
                                        icon: (
                                            <Receipt1 size="28" color="#6F739B" variant="TwoTone" />
                                        ),
                                    },
                                ]}
                            />

                            <div className="hidden pt-6 mt-auto xl:block">
                                <AdsCarousel
                                    className="h-auto xl:h-[193px]" // was "h-[193px]"
                                    items={[
                                        <USDCAd
                                            key="usdc-1"
                                            onCta={() => navigate("/wallets")}
                                            RightVisual={UsdcRightMock}
                                        />,
                                        <KESUSDAd
                                            key="KSHUSD-1"
                                            onCta={() => navigate("/wallets")}
                                            RightVisual={KESUSDRightMock}
                                        />,
                                    ]}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Full-width ad only on md (tablet). Hidden on sm & lg+ */}
                    <div className="block col-span-12 xl:hidden">
                        <AdsCarousel
                            className="h-auto xl:h-[193px]"
                            items={[
                                <USDCAd
                                    key="usdc-1"
                                    onCta={() => navigate("/wallets")}
                                    RightVisual={UsdcRightMock}
                                />,
                                <KESUSDAd
                                    key="KSHUSD-1"
                                    onCta={() => navigate("/wallets")}
                                    RightVisual={KESUSDRightMock}
                                />,
                            ]}
                        />
                    </div>
                </div>
            </section>

            <DashboardModals
                // toggles
                showDrawer={showDrawer}
                setShowDrawer={setShowDrawer}
                showFundModal={showFundModal}
                setShowFundModal={setShowFundModal}
                showFundingMethodModal={showFundingMethodModal}
                setShowFundingMethodModal={setShowFundingMethodModal}
                showReceiveWalletModal={showReceiveWalletModal}
                setShowReceiveWalletModal={setShowReceiveWalletModal}
                showFundViaConversionModal={showFundViaConversionModal}
                setShowFundViaConversionModal={setShowFundViaConversionModal}
                showGenerateModal={showGenerateModal}
                setShowGenerateModal={setShowGenerateModal}
                showSuccessModal={showSuccessModal}
                setShowSuccessModal={setShowSuccessModal}
                showPinModal={showPinModal}
                setShowPinModal={setShowPinModal}
                // data
                accountNumber={accountNumber}
                accountName={accountName}
                bankAddress={bankAddress}
                bankCode={bankCode}
                routingNumber={routingNumber}
                paymentRails={paymentRails}
                bankName={bankName}
                account={account}
                defaults={defaults}
                userEmail={userProfile?.data?.email || userProfile?.email || ""}
                onPinSuccess={onPinSuccess}
                setOnPinSuccess={setOnPinSuccess}
                pinError={pinError}
                setPinError={setPinError}
                branchName={branchName}
        />

        {showOutstandingFeeModal && feesToDisplay.length > 0 && (
          <OutstandingMaintenanceFeeModal
            isOpen={showOutstandingFeeModal}
            onClose={handleCloseMaintenaceFeeModal}
            onFundWallet={() => {
              handleCloseMaintenaceFeeModal();
              const targetCurrency =
                feesToDisplay.find(
                  (f) => String(f.currency || "").toUpperCase() === String(defaults || "").toUpperCase()
                )?.currency ||
                feesToDisplay?.[0]?.currency

              if (targetCurrency) {
                dispatch({ type: "CURRENCY", payload: targetCurrency });
                localStorage.setItem("default", targetCurrency);

                const isCrypto = targetCurrency === "USDT" || targetCurrency === "USDC";
                if (isCrypto) {
                  setShowFundingMethodModal(true);
                } else {
                  setShowFundModal(true);
                }
              }
            }}
            feesToDisplay={feesToDisplay}
          />
        )}
        </>
    );
}
